/**
 * AI model catalogue — served as a STATIC ASSET, never fetched from the provider.
 *
 * Listing models used to go through clientSecretRequest: browser -> skapi queue
 * -> proxy -> provider, seconds of latency on every chat visit and every
 * platform switch, to populate a dropdown whose contents change a few times a
 * year. The catalogue now ships in `public/models/<platform>.json` (the
 * provider's own response body, verbatim, so every consumer keeps reading
 * `.data[]` with `id` / `display_name` / `max_input_tokens`), which the browser
 * fetches from our own origin and caches.
 *
 * To refresh it: call the provider's models endpoint and save the response body
 * over the matching file. Nothing else needs to change.
 *
 * THERE IS NO PROVIDER CALL LEFT IN HERE, deliberately. A model-listing call was
 * previously reused as an "is this API key valid" probe, and that is what made
 * "Your API key is invalid" untrustworthy: the probe fails for every reason a
 * network call can fail (the skapi queue, the proxy, a timeout, a provider
 * outage, a rate limit), and each one was reported to the user as a bad key. The
 * key verdict now comes from real chat traffic instead — see
 * reportProviderError / apiKeyRejected below.
 */

export type AiModelEntry = {
	id: string;
	display_name?: string;
	max_input_tokens?: number;
	[k: string]: any;
};

export type AiPlatform = 'claude' | 'openai';

const CATALOGUE_URL: Record<AiPlatform, string> = {
	claude: '/models/claude.json',
	openai: '/models/openai.json',
};

// Resolved once per page load and shared by every consumer (the chat picker,
// the project settings picker). The in-flight promise is the dedupe: two views
// mounting together must not both fetch.
const catalogueCache: Partial<Record<AiPlatform, AiModelEntry[]>> = {};
const catalogueInflight: Partial<Record<AiPlatform, Promise<AiModelEntry[]>>> = {};

/**
 * The platform's model list. Resolves with `[]` rather than throwing: a missing
 * or malformed catalogue must degrade to "no models to pick from", never break
 * the view that asked.
 */
export function loadModelCatalogue(platform: AiPlatform): Promise<AiModelEntry[]> {
	const cached = catalogueCache[platform];
	if (cached) return Promise.resolve(cached);
	const inflight = catalogueInflight[platform];
	if (inflight) return inflight;

	const url = CATALOGUE_URL[platform];
	const p = fetch(url, { credentials: 'omit' })
		.then((res) => (res.ok ? res.json() : null))
		.then((body: any) => {
			const list: AiModelEntry[] = Array.isArray(body?.data)
				? body.data.filter((m: any) => m && typeof m.id === 'string')
				: [];
			if (list.length) catalogueCache[platform] = list;
			return list;
		})
		.catch((err) => {
			console.warn('[ai_models] model catalogue unavailable', platform, err);
			return [] as AiModelEntry[];
		});
	catalogueInflight[platform] = p;
	// Not .finally(): this project's TS lib target predates it.
	p.then(
		() => { delete catalogueInflight[platform]; },
		() => { delete catalogueInflight[platform]; },
	);
	return p;
}

/* ── API-key verdict, driven by real requests ─────────────────────────────── */
//
// Only a request the user actually made can prove a key is bad, and only a
// provider answer that NAMES the key as the problem counts. Everything else (a
// timeout, a 429, a 502, a dropped connection) leaves the verdict untouched, so
// a transient failure can no longer accuse the user's key.
//
// Keyed by project+platform because a project can hold one key per platform.

const rejected: Record<string, boolean> = {};
const verdictKey = (service: string, platform: AiPlatform) => service + '#' + platform;

/** True once the provider has rejected this project's key on a real request. */
export function apiKeyRejected(service: string, platform: AiPlatform): boolean {
	return !!rejected[verdictKey(service, platform)];
}

/**
 * Record the outcome of a real provider request.
 *
 * `isKeyError` must come from the engine's isProviderApiKeyError, which matches
 * only the provider's key-specific markers (`authentication_error`,
 * `invalid_api_key`, "incorrect api key"), never a bare 401 — our own MCP bearer
 * expiring is also a 401 and is refreshed automatically.
 *
 * Returns true when the stored verdict changed, so a caller can react without
 * re-rendering on every message.
 */
export function reportProviderError(
	service: string,
	platform: AiPlatform,
	isKeyError: boolean,
): boolean {
	const k = verdictKey(service, platform);
	if (isKeyError) {
		if (rejected[k]) return false;
		rejected[k] = true;
		return true;
	}
	return false;
}

/** A request SUCCEEDED, so whatever we believed about the key is obsolete. */
export function reportProviderSuccess(service: string, platform: AiPlatform): boolean {
	const k = verdictKey(service, platform);
	if (!rejected[k]) return false;
	delete rejected[k];
	return true;
}

/** Forget the verdict — call after the key is changed or removed. */
export function invalidateApiKeyVerdict(service: string, platform: AiPlatform): void {
	delete rejected[verdictKey(service, platform)];
}
